import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { resetUserPassword } from '../../api_calls/api_calls_auth';
import { useLanguageContext } from '../../hooks/useLanguageContext';
import { multilingual } from '../../multilingual';

const ResetPassword = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { language } = useLanguageContext();
    const dictionary = multilingual[language]?.signin ?? multilingual.gr.signin;

    const [values, setValues] = useState({ username: '', newPassword: '' });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues((prev) => ({ ...prev, [name]: value }));
        if (value) setErrors((prev) => ({ ...prev, [name]: false }));
    };

    const validate = () => {
        const newErrors = {};
        if (!values.username) newErrors.username = true;
        if (!values.newPassword) newErrors.newPassword = true;
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage('');
        setSuccess(false);
        if (!validate()) {
            setMessage(dictionary.reset_error_fill_fields || dictionary.error_fill_fields);
            return;
        }
        setLoading(true);
        try {
            await resetUserPassword({
                username: values.username,
                newPassword: values.newPassword,
            });
            setMessage(dictionary.reset_success || 'Password updated successfully!');
            setSuccess(true);
            setValues({ username: '', newPassword: '' });
        } catch {
            setMessage(dictionary.reset_error || 'Failed to reset password. Please try again.');
            setSuccess(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <CssBaseline />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    minHeight: '100vh',
                    backgroundColor: '#f3f4f6',
                    p: 0,
                }}
            >
                <Box
                    sx={{
                        width: { xs: '0%', md: '50%' },
                        backgroundImage: `url(https://eu-images.contentstack.com/v3/assets/blt31d6b0704ba96e9d/blt88a7087d82cacbf3/655e23392ab1fb0407ec12a9/GettyImages-582763822.jpg?width=850&auto=webp&quality=95&format=jpg&disable=upscale)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: { xs: 'none', md: 'block' },
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            background: `linear-gradient(180deg, ${alpha(theme.palette.common.black, 0.22)} 0%, ${alpha(theme.palette.secondary.main, 0.18)} 100%)`,
                            zIndex: 1,
                        },
                    }}
                >
                </Box>
                <Box
                    sx={{
                        width: { xs: '100%', md: '50%' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: { xs: 3, sm: 6, md: 8 },
                    }}
                >
                    <Fade in timeout={500}>
                        <Box sx={{ width: '100%', maxWidth: 460 }}>
                            <Typography
                                component="h1"
                                variant="h3"
                                sx={{ fontWeight: 800, fontSize: { xs: 36, md: 48 }, lineHeight: 1.05 }}
                            >
                                {dictionary.reset_title || 'Reset password'}
                            </Typography>
                            <Typography sx={{ mt: 1.5, color: '#6b7280', fontSize: 22 }}>
                                {dictionary.reset_subtitle || 'Set a new password for your account.'}
                            </Typography>
                            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 5 }}>
                                <Typography sx={{ mb: 1, fontWeight: 700, color: '#374151' }}>
                                    {dictionary.username}
                                </Typography>
                                <TextField
                                    onChange={handleChange}
                                    variant="outlined"
                                    fullWidth
                                    id="reset-username"
                                    type="text"
                                    placeholder={dictionary.username_placeholder || 'e.g. manager@aimestate.com'}
                                    name="username"
                                    autoComplete="username"
                                    autoFocus
                                    error={!!errors.username}
                                    value={values.username}
                                    InputProps={{
                                        sx: {
                                            borderRadius: 2,
                                            backgroundColor: '#e5e7eb',
                                        },
                                    }}
                                    sx={{ mb: 3 }}
                                />
                                <Typography sx={{ mb: 1, fontWeight: 700, color: '#374151' }}>
                                    {dictionary.reset_new_password || 'New password'}
                                </Typography>
                                <TextField
                                    onChange={handleChange}
                                    variant="outlined"
                                    fullWidth
                                    id="reset-new-password"
                                    type={!showPassword ? 'password' : 'text'}
                                    placeholder="********"
                                    name="newPassword"
                                    autoComplete="new-password"
                                    error={!!errors.newPassword}
                                    value={values.newPassword}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)}>
                                                    {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            borderRadius: 2,
                                            backgroundColor: '#e5e7eb',
                                        },
                                    }}
                                />
                                {!!message && (
                                    <Alert
                                        severity={success ? 'success' : 'error'}
                                        sx={{
                                            mt: 2,
                                            ...(success
                                                ? { border: '1px solid', borderColor: 'success.main' }
                                                : {}),
                                        }}
                                    >
                                        {message}
                                    </Alert>
                                )}
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    sx={{
                                        color: 'white',
                                        mt: 4,
                                        width: '100%',
                                        height: 52,
                                        borderRadius: 2,
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                    boxShadow: `0 10px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: 20,
                                        '&:hover': {
                                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                        },
                                    }}
                                    disabled={loading}
                                    endIcon={<ArrowForwardIcon />}
                                >
                                    {loading ? (dictionary.reset_loading || 'Updating...') : (dictionary.reset_button || 'Reset password')}
                                </Button>
                                <Button
                                    fullWidth
                                    variant="text"
                                    startIcon={<ArrowBackIcon />}
                                    sx={{ mt: 1, textTransform: 'none' }}
                                    onClick={() => navigate('/signin')}
                                >
                                    {dictionary.back_to_signin || 'Back to sign in'}
                                </Button>
                            </Box>
                        </Box>
                    </Fade>
                </Box>
            </Box>
        </>
    );
};

export default ResetPassword;
